import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { TextField, Button, Select, MenuItem, InputLabel, FormControl, Menu, RadioGroup, FormControlLabel, Radio, useTheme, IconButton, Tooltip, Chip } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
// ClearIcon removed — using radio + single input instead
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';
import { useAuth } from '../../contexts/AuthContext';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import QAModal from '../qa/QAModal';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScienceIcon from '@mui/icons-material/Science';
import LanguageIcon from '@mui/icons-material/Language';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailIcon from '@mui/icons-material/Email';
import SlackSvg from '../../assets/slack.svg';
import MsTeamsSvg from '../../assets/msteams.svg';
import { formatQuickReplies, getQuickReplyLabel, isQuickReplySelected } from './quickReplyTranscript';

function WhatsAppChannelIcon({ sx = {} }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                borderRadius: '4px',
                bgcolor: '#25D366',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                mr: 0.5,
                verticalAlign: 'middle',
                ...sx
            }}
            aria-label="WhatsApp"
        >
            W
        </Box>
    );
}


// Helper to format timestamp to MM-dd-yyyy hh:mm
function formatDate(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    const pad = n => n < 10 ? '0' + n : n;
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const yyyy = d.getFullYear();
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${MM}-${dd}-${yyyy} ${hh}:${mm}`;
}

function formatEndUserGeo(chatSession) {
    if (!chatSession) return null;
    const city = chatSession.endUserCity ?? chatSession.end_user_city;
    const state = chatSession.endUserState ?? chatSession.end_user_state;
    const country = chatSession.endUserCountry ?? chatSession.end_user_country;
    const parts = [city, state, country].filter(v => v != null && String(v).trim() !== '');
    return parts.length > 0 ? parts.join(', ') : null;
}

export function HistoricalQuickReplies({ quickReplies, followingUserMessage }) {
    if (!Array.isArray(quickReplies) || quickReplies.length === 0) return null;
    const replies = quickReplies
        .map((reply, idx) => ({ reply, idx, label: getQuickReplyLabel(reply) }))
        .filter(item => item.label);
    if (replies.length === 0) return null;
    const selectedReply = replies.find(({ reply }) => isQuickReplySelected(reply, followingUserMessage));

    return (
        <Box
            sx={{
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '100%',
                mt: 1,
                mb: 1.5,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'action.hover',
                textAlign: 'left',
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left' }}>
                Quick replies
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                {replies.map(({ reply, idx, label }) => {
                    const selected = isQuickReplySelected(reply, followingUserMessage);
                    return (
                        <Chip
                            key={`${label}-${idx}`}
                            label={label}
                            size="small"
                            color="primary"
                            variant={selected ? 'filled' : 'outlined'}
                            aria-label={selected ? `${label} (selected)` : label}
                            sx={{ pointerEvents: 'none' }}
                        />
                    );
                })}
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75, overflowWrap: 'anywhere', textAlign: 'left' }}
            >
                {selectedReply ? `Selected: ${selectedReply.label}` : 'No selection recorded'}
            </Typography>
        </Box>
    );
}

export function buildTimestampedChatHistory(chatData) {
    const parseChatDataLocal = data => {
        if (!data) return [];
        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (Array.isArray(parsed)) return parsed;
            if (parsed && Array.isArray(parsed.messages)) return parsed.messages;
            return [];
        } catch {
            return [];
        }
    };

    const parsed = parseChatDataLocal(chatData);
    if (!Array.isArray(parsed) || parsed.length === 0) return '';

    const sortedChat = [...parsed].sort((a, b) => {
        const tsA = a.timestamp || '';
        const tsB = b.timestamp || '';
        if (!tsA && !tsB) return 0;
        if (!tsA) return 1;
        if (!tsB) return -1;
        return new Date(tsA).getTime() - new Date(tsB).getTime();
    });
    const followingUserMessages = new Array(sortedChat.length);
    let followingUserMessage;
    for (let idx = sortedChat.length - 1; idx >= 0; idx -= 1) {
        followingUserMessages[idx] = followingUserMessage;
        if (typeof sortedChat[idx].role === 'string' && sortedChat[idx].role.toLowerCase() === 'user') {
            followingUserMessage = sortedChat[idx];
        }
    }

    const toPlainText = html => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return (tmp.textContent || tmp.innerText || '').trim();
    };

    return sortedChat
        .map((msg, idx) => {
            const rawText = msg.text || msg.content || '';
            const text = toPlainText(rawText);
            if (!text) return null;
            const rolePrefix = msg.role === 'user' ? 'User:' : 'Bot:';
            const ts = formatDate(msg.timestamp);
            const quickReplies = msg.role === 'user' ? '' : formatQuickReplies(msg.quickReplies, followingUserMessages[idx]);
            return `${ts ? `${ts}\n` : ''}${rolePrefix} ${text}${quickReplies ? `\n\n${quickReplies}` : ''}`;
        })
        .filter(Boolean)
        .join('\n\n');
}

const DATE_PRESETS = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'Last 7 Days', value: 'last7' },
    { label: 'Month To Date', value: 'monthToDate' },
    { label: 'Last 30 Days', value: 'last30' },
    { label: 'Last 60 Days', value: 'last60' },
    { label: 'Custom Range', value: 'custom' },
];

function getPresetRange(preset, dateFrom, dateTo) {
    const today = dayjs();
    switch (preset) {
        case 'today':
            return { from: today.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
        case 'yesterday':
            const yest = today.subtract(1, 'day');
            return { from: yest.format('YYYY-MM-DD'), to: yest.format('YYYY-MM-DD') };
        case 'thisWeek':
            // Week starts Monday
            const startOfWeek = today.startOf('week').add(1, 'day');
            return { from: startOfWeek.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
        case 'last7':
            return { from: today.subtract(6, 'day').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
        case 'monthToDate':
            return { from: today.startOf('month').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
        case 'last30':
            return { from: today.subtract(29, 'day').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
        case 'last60':
            return { from: today.subtract(59, 'day').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
        case 'custom':
        default:
            return { from: dateFrom, to: dateTo };
    }
}

export default function ChatLogs() {
    const theme = useTheme();
    const { user } = useAuth();
    const chatLogAccessOnly = !!user?.chat_log_access_only;
    // Save handler for QAModal
    const handleQASave = async (modalData) => {
        // show both the small "loading" disables and the full-screen QA processing overlay
        setLoading(true);
        setProcessingQA(true);
        const jwtToken = Cookies.getJwtToken();
        try {
            // Check for duplicate questions
            const isDuplicate = qaList.some(
                qa =>
                    qa.question.trim().toLowerCase() === modalData.question.trim().toLowerCase() &&
                    (modalData.id ? qa.id !== modalData.id : true)
            );
            if (isDuplicate) {
                setLoading(false);
                alert('A Q&A with this question already exists.');
                return;
            }
            // Wrap Q&A fields in qaKnowledge, send kbIds as sibling property
            const payload = {
                qaKnowledge: {
                    ...modalData,
                    createdBy: null, // backend will set from JWT
                    updatedBy: null
                },
                kbIds: modalData.kbIds
            };
            if (qaModalMode === 'edit') {
                await axios.put(`${API_URL}/qa/update`, payload, {
                    headers: { Authorization: `Bearer ${jwtToken}` }
                });
            } else {
                await axios.post(`${API_URL}/qa/add`, payload, {
                    headers: { Authorization: `Bearer ${jwtToken}` }
                });
                // Remove unanswered entry if present
                if (modalData.id) {
                    await axios.delete(`${API_URL}/qa/unanswered/delete/${modalData.id}`, {
                        headers: { Authorization: `Bearer ${jwtToken}` }
                    });
                }
            }
            // Refresh lists
            const qaRes = await axios.get(`${API_URL}/qa/all`, {
                headers: { Authorization: `Bearer ${jwtToken}` }
            });
            setQaList(qaRes.data || []);
            const unansweredRes = await axios.get(`${API_URL}/qa/unanswered/all`, {
                headers: { Authorization: `Bearer ${jwtToken}` }
            });
            setUnansweredList(unansweredRes.data || []);
            setProcessingQA(false);
            setLoading(false);
            setQaModalOpen(false);
        } catch (err) {
            setProcessingQA(false);
            setLoading(false);
            alert('An error occurred while saving the Q&A.');
        }
    };
    // Modal state for training
    const [qaModalOpen, setQaModalOpen] = useState(false);
    const [qaModalMode, setQaModalMode] = useState('edit'); // 'edit' or 'answer'
    const [qaModalData, setQaModalData] = useState(null);
    // Q&A and unanswered state
    const [qaList, setQaList] = useState([]);
    const [unansweredList, setUnansweredList] = useState([]);
    const [sessionTypeFilter, setSessionTypeFilter] = useState('all'); // 'all', 'internal', 'live', 'aimode', ...
    const [sessionInternalMap, setSessionInternalMap] = useState({}); // sessionId -> boolean
    const [sessionAiModeMap, setSessionAiModeMap] = useState({}); // sessionId -> boolean
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [agents, setAgents] = useState([]);
    const [agentMap, setAgentMap] = useState({}); // agentId -> agentName
    const [selectedAgent, setSelectedAgent] = useState('');
    const [searchText, setSearchText] = useState('');
    const [searchMode, setSearchMode] = useState('chat'); // 'chat' or 'session'
    const [datePreset, setDatePreset] = useState('last7');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [chatLogs, setChatLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    // Separate flag for showing the Q&A processing overlay so we don't
    // interfere with the general `loading` state used for initial fetches
    // and searching (which should continue to show the small "Loading..."
    // indicators in each panel).
    const [processingQA, setProcessingQA] = useState(false);
    const [selectedLog, setSelectedLogState] = useState(null);
    const [sessionLogData, setSessionLogData] = useState(null);
    const { sessionId: routeSessionId } = useParams();

    // Wrapper for selecting a log and fetching sessionLog
    const setSelectedLog = async (log) => {
        setSelectedLogState(log);
        setSessionLogData(null);
        if (log && log.sessionId) {
            try {
                const res = await axios.post(
                    `${API_URL}/search/sessionLog?sessionId=${encodeURIComponent(log.sessionId)}`,
                    {},
                    {
                        headers: {
                            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                        }
                    }
                );
                setSessionLogData(res.data);
                console.log('Session log data:', res.data);
                if (res.data?.chatSession?.internal !== undefined) {
                    setSessionInternalMap(prev => ({ ...prev, [log.sessionId]: res.data.chatSession.internal }));
                }
                if (res.data?.chatSession?.aiMode !== undefined) {
                    setSessionAiModeMap(prev => ({ ...prev, [log.sessionId]: res.data.chatSession.aiMode }));
                }
            } catch (err) {
                setSessionLogData(null);
                console.error('Failed to fetch session log:', err);
            }
        }
    };

    const handleCopyChatHistory = async () => {
        const text = buildTimestampedChatHistory(selectedLog?.chatData) || selectedLog?.chatHistoryText || '';
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy chat history:', err);
        }
    };

    // Helper to parse chatData (handles { messages: [...] } structure)
    function parseChatData(chatData) {
        if (!chatData) return [];
        try {
            const parsed = typeof chatData === 'string' ? JSON.parse(chatData) : chatData;
            if (Array.isArray(parsed)) return parsed;
            if (parsed && Array.isArray(parsed.messages)) return parsed.messages;
            return [];
        } catch {
            return [];
        }
    }

    // Minimal heuristic to skip greetings/idle messages
    const greetingSet = new Set(['hi', 'hello', 'hey', 'thanks', 'thank you', 'bye', 'good morning', 'good evening', 'good night']);
    function isGreetingOrIdle(text) {
        if (!text) return true;
        const t = (text || '').trim().toLowerCase();
        if (t.length < 3) return true;
        // exact greetings
        if (greetingSet.has(t)) return true;
        return false;
    }

    function isTrainableUserMessage(userMsg, agentReply) {
        if (!userMsg || !agentReply) return false;
        const txt = (userMsg.text || userMsg.content || '').trim();
        if (!txt) return false;
        // skip very short messages unless they end with a question mark
        const words = txt.split(/\s+/).filter(Boolean);
        if (txt.length < 10 && words.length <= 2 && !txt.endsWith('?')) return false;
        if (isGreetingOrIdle(txt)) return false;
        return true;
    }

    function toMMDDYYYY(dateStr) {
        if (!dateStr) return null;
        const [yyyy, MM, dd] = dateStr.split('-');
        return `${MM}-${dd}-${yyyy}`;
    }

    function getChannelFromSessionId(sessionId) {
        if (!sessionId) return null;
        if (sessionId.startsWith('botframework-') || sessionId.startsWith('msteams-')) return 'teams';
        if (sessionId.startsWith('slack-')) return 'slack';
        if (sessionId.startsWith('email-')) return 'email';
        if (sessionId.startsWith('whatsapp-')) return 'whatsapp';
        return null;
    }

    // Accepts optional overrideSessionId for direct search (deep-link only)
    const handleSearch = async (overrideSessionId = null) => {
        setLoading(true);
        try {
            // Determine date range
            let range = datePreset === 'custom' ? { from: dateFrom, to: dateTo } : getPresetRange(datePreset, dateFrom, dateTo);
            let params;
            if (overrideSessionId !== null) {
                // Deep-link search: use overrideSessionId
                params = {
                    searchText: null,
                    agentId: selectedAgent ? selectedAgent : 0,
                    dateFrom: toMMDDYYYY(range.from) || null,
                    dateTo: toMMDDYYYY(range.to) || null,
                    sessionId: overrideSessionId
                };
            } else {
                // Normal UI search: use current state
                params = {
                    searchText: searchMode === 'session' ? null : (searchText || null),
                    agentId: selectedAgent ? selectedAgent : 0,
                    dateFrom: toMMDDYYYY(range.from) || null,
                    dateTo: toMMDDYYYY(range.to) || null,
                    sessionId: searchMode === 'session' ? (searchText || null) : null
                };
            }
            console.log('[ChatLogs] handleSearch params:', params);
            const res = await axios.post(`${API_URL}/search/chatLogs`, params, {
                headers: {
                    'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                }
            });
            let logs = res.data || [];
            console.log('[ChatLogs] handleSearch backend response:', logs);
            // Always clear selected log before updating logs
            setSelectedLogState(null);
            setChatLogs(logs);
            if (logs.length > 0) {
                setSelectedLog(logs[0]);
                console.log('[ChatLogs] setSelectedLog:', logs[0]);
            } else {
                setSelectedLogState(null);
                console.log('[ChatLogs] No logs found');
            }
        } catch (err) {
            setChatLogs([]);
            console.error('[ChatLogs] handleSearch error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Export logic
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const exportAsJSON = () => {
        const dataStr = JSON.stringify(chatLogs, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chatLogs.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        handleExportClose();
    };

    const exportAsCSV = () => {
        if (!chatLogs || chatLogs.length === 0) return;
        // Flatten objects for CSV
        const keys = Object.keys(chatLogs[0]);
        const csvRows = [keys.join(',')];
        chatLogs.forEach(row => {
            const vals = keys.map(k => {
                let v = row[k];
                if (typeof v === 'object') v = JSON.stringify(v);
                if (typeof v === 'string') v = '"' + v.replace(/"/g, '""') + '"';
                return v;
            });
            csvRows.push(vals.join(','));
        });
        const csvStr = csvRows.join('\n');
        const blob = new Blob([csvStr], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chatLogs.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        handleExportClose();
    };

    useEffect(() => {
        // If deep-linking by sessionId, skip the initial chatLogs fetch to avoid overwriting the session search result
        if (routeSessionId) {
            // Only fetch agents, QAs, and unanswered QAs
            setLoading(true);
            Promise.all([
                (async () => {
                    try {
                        const res = await axios.post(`${API_URL}/agents/getAllAgents`, {}, {
                            headers: {
                                'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                            }
                        });
                        const agentsArr = res.data || [];
                        setAgents(agentsArr);
                        // Build agentId -> agentName map
                        const map = {};
                        agentsArr.forEach(agent => {
                            map[agent.id] = agent.agentName;
                        });
                        setAgentMap(map);
                    } catch (err) {
                        setAgents([]);
                        setAgentMap({});
                    }
                })(),
                (async () => {
                    try {
                        const jwtToken = Cookies.getJwtToken();
                        const qaRes = await axios.get(`${API_URL}/qa/all`, {
                            headers: { Authorization: `Bearer ${jwtToken}` }
                        });
                        setQaList(qaRes.data || []);
                    } catch (err) {
                        setQaList([]);
                    }
                })(),
                (async () => {
                    try {
                        const jwtToken = Cookies.getJwtToken();
                        const unansweredRes = await axios.get(`${API_URL}/qa/unanswered/all`, {
                            headers: { Authorization: `Bearer ${jwtToken}` }
                        });
                        setUnansweredList(unansweredRes.data || []);
                    } catch (err) {
                        setUnansweredList([]);
                    }
                })()
            ]).finally(() => {
                setLoading(false);
            });
        } else {
            // No sessionId in route, do the full initial fetch with 'Last 7 Days' range
            setLoading(true);
            const last7 = getPresetRange('last7');
            Promise.all([
                (async () => {
                    try {
                        const res = await axios.post(`${API_URL}/agents/getAllAgents`, {}, {
                            headers: {
                                'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                            }
                        });
                        const agentsArr = res.data || [];
                        setAgents(agentsArr);
                        // Build agentId -> agentName map
                        const map = {};
                        agentsArr.forEach(agent => {
                            map[agent.id] = agent.agentName;
                        });
                        setAgentMap(map);
                    } catch (err) {
                        setAgents([]);
                        setAgentMap({});
                    }
                })(),
                (async () => {
                    try {
                        const params = {
                            searchText: searchText || null,
                            sessionId: null,
                            agentId: selectedAgent ? selectedAgent : 0,
                            dateFrom: toMMDDYYYY(last7.from) || null,
                            dateTo: toMMDDYYYY(last7.to) || null
                        };
                        const res = await axios.post(`${API_URL}/search/chatLogs`, params, {
                            headers: {
                                'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                            }
                        });
                        const logs = res.data || [];
                        setChatLogs(logs);
                        if (logs.length > 0) {
                            setSelectedLog(logs[0]);
                        } else {
                            setSelectedLogState(null);
                        }
                    } catch (err) {
                        setChatLogs([]);
                    }
                })(),
                (async () => {
                    try {
                        const jwtToken = Cookies.getJwtToken();
                        const qaRes = await axios.get(`${API_URL}/qa/all`, {
                            headers: { Authorization: `Bearer ${jwtToken}` }
                        });
                        setQaList(qaRes.data || []);
                    } catch (err) {
                        setQaList([]);
                    }
                })(),
                (async () => {
                    try {
                        const jwtToken = Cookies.getJwtToken();
                        const unansweredRes = await axios.get(`${API_URL}/qa/unanswered/all`, {
                            headers: { Authorization: `Bearer ${jwtToken}` }
                        });
                        setUnansweredList(unansweredRes.data || []);
                    } catch (err) {
                        setUnansweredList([]);
                    }
                })()
            ]).finally(() => {
                setLoading(false);
            });
        }
        // eslint-disable-next-line
    }, []);

    // If the route contains a sessionId param, set searchMode and searchText
    useEffect(() => {
        if (routeSessionId) {
            setSearchMode('session');
            setSearchText(routeSessionId);
        }
        // eslint-disable-next-line
    }, [routeSessionId]);

    // Trigger search only after both searchMode and searchText are set from routeSessionId
    useEffect(() => {
        if (
            routeSessionId &&
            searchMode === 'session' &&
            searchText === routeSessionId
        ) {
            handleSearch(routeSessionId);
        }
        // eslint-disable-next-line
    }, [routeSessionId, searchMode, searchText]);

    // Remove duplicate fetchAgents effect, now handled in initial useEffect above

    // Fetch internal status for all chatLogs
    useEffect(() => {
        if (!chatLogs || chatLogs.length === 0) return;

        const logsNeedingFetch = chatLogs.filter(
            log =>
                (log.internal === undefined && sessionInternalMap[log.sessionId] === undefined)
                || (log.aiMode === undefined && sessionAiModeMap[log.sessionId] === undefined)
        );

        if (logsNeedingFetch.length === 0) return;

        (async () => {
            const jwtToken = Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token';
            const newMap = { ...sessionInternalMap };
            const newAiModeMap = { ...sessionAiModeMap };

            await Promise.all(
                logsNeedingFetch.map(async log => {
                    try {
                        const res = await axios.post(
                            `${API_URL}/search/sessionLog?sessionId=${encodeURIComponent(log.sessionId)}`,
                            {},
                            { headers: { Authorization: `Bearer ${jwtToken}` } }
                        );
                        if (res.data?.chatSession?.internal !== undefined) {
                            newMap[log.sessionId] = res.data.chatSession.internal;
                        }
                        if (res.data?.chatSession?.aiMode !== undefined) {
                            newAiModeMap[log.sessionId] = res.data.chatSession.aiMode;
                        }
                    } catch (err) {
                        console.error('Failed to fetch session internal status:', err);
                    }
                })
            );

            setSessionInternalMap(newMap);
            setSessionAiModeMap(newAiModeMap);
        })();
        // eslint-disable-next-line
    }, [chatLogs]);

    // Compute filtered logs based on sessionTypeFilter
    const filteredLogs = chatLogs.filter(log => {
        if (sessionTypeFilter === 'all') return true;
        const internalVal = log.internal !== undefined ? log.internal : sessionInternalMap[log.sessionId];
        const aiModeVal = log.aiMode !== undefined ? log.aiMode : sessionAiModeMap[log.sessionId];
        const channel = getChannelFromSessionId(log.sessionId);
        if (sessionTypeFilter === 'internal') return internalVal === true;
        if (sessionTypeFilter === 'aimode') return aiModeVal === true && !['teams', 'slack', 'email', 'whatsapp'].includes(channel);
        if (sessionTypeFilter === 'live') return internalVal === false && aiModeVal !== true && channel !== 'email';
        if (sessionTypeFilter === 'teams') return channel === 'teams';
        if (sessionTypeFilter === 'slack') return channel === 'slack';
        if (sessionTypeFilter === 'email') return channel === 'email';
        if (sessionTypeFilter === 'whatsapp') return channel === 'whatsapp';
        return true;
    });

    return (
        <Box sx={{ width: '100%', p: 2, height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Session Type Filter Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', p: 1.5, mb: 1, gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Session Type:</Typography>
                <ToggleButtonGroup
                    value={sessionTypeFilter}
                    exclusive
                    onChange={(e, val) => { if (val !== null) setSessionTypeFilter(val); }}
                    size="small"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="internal"><ScienceIcon fontSize="small" sx={{ mr: 0.5, color: '#2196F3' }} />Internal Test</ToggleButton>
                    <ToggleButton value="live"><LanguageIcon fontSize="small" sx={{ mr: 0.5, color: '#FF9800' }} />Live Visitor</ToggleButton>
                    <ToggleButton value="aimode"><AutoAwesomeIcon fontSize="small" sx={{ mr: 0.5, color: '#9C27B0' }} />Ai Mode</ToggleButton>
                    <ToggleButton value="teams"><img src={MsTeamsSvg} alt="Teams" style={{ width: '16px', height: '16px', marginRight: '4px' }} />Teams</ToggleButton>
                    <ToggleButton value="slack"><img src={SlackSvg} alt="Slack" style={{ width: '16px', height: '16px', marginRight: '4px' }} />Slack</ToggleButton>
                    <ToggleButton value="email"><EmailIcon fontSize="small" sx={{ mr: 0.5, color: '#1565C0' }} />Email</ToggleButton>
                    {/* <ToggleButton value="whatsapp"><WhatsAppChannelIcon />WhatsApp</ToggleButton> */}
                </ToggleButtonGroup>
            </Box>
            {/* Search / Filter Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', p: 2, mb: 2, gap: 2 }}>
                <FormControl component="fieldset" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                    <RadioGroup row value={searchMode} onChange={e => setSearchMode(e.target.value)}>
                        <FormControlLabel value="chat" control={<Radio />} label="Chat Log" />
                        <FormControlLabel value="session" control={<Radio />} label="Session ID" />
                    </RadioGroup>
                </FormControl>
                <TextField
                    label={searchMode === 'session' ? 'Session ID' : 'Search Text'}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    sx={{ minWidth: 300 }}
                />
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel id="agent-select-label">Agent</InputLabel>
                    <Select
                        labelId="agent-select-label"
                        value={selectedAgent}
                        label="Agent"
                        onChange={e => setSelectedAgent(e.target.value)}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {agents.map(agent => (
                            <MenuItem key={agent.id} value={agent.id}>{agent.agentName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel id="date-preset-label">Date Range</InputLabel>
                    <Select
                        labelId="date-preset-label"
                        value={datePreset}
                        label="Date Range"
                        onChange={e => setDatePreset(e.target.value)}
                    >
                        {DATE_PRESETS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {datePreset === 'custom' && (
                    <>
                        <TextField
                            label="Date From"
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 150 }}
                        />
                        <TextField
                            label="Date To"
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 150 }}
                        />
                    </>
                )}
                <Button variant="contained" color="primary" sx={{ minWidth: 100 }} onClick={() => handleSearch()} disabled={loading}>Search</Button>
                <Button variant="outlined" color="secondary" sx={{ minWidth: 100 }} disabled={loading || chatLogs.length === 0} onClick={handleExportClick}>Export</Button>
                <Button
                    variant="outlined"
                    color="inherit"
                    sx={{ minWidth: 100 }}
                    onClick={() => {
                        setSearchText('');
                        setSelectedAgent('');
                        setSessionTypeFilter('all');
                        setDatePreset('last7');
                        setDateFrom('');
                        setDateTo('');
                        setLoading(true);
                        // Re-fetch logs for last 7 days
                        (async () => {
                            try {
                                const last7 = getPresetRange('last7');
                                const params = {
                                    searchText: null,
                                    sessionId: null,
                                    agentId: 0,
                                    dateFrom: toMMDDYYYY(last7.from) || null,
                                    dateTo: toMMDDYYYY(last7.to) || null
                                };
                                const res = await axios.post(`${API_URL}/search/chatLogs`, params, {
                                    headers: {
                                        'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                                    }
                                });
                                const logs = res.data || [];
                                setChatLogs(logs);
                                if (logs.length > 0) {
                                    setSelectedLog(logs[0]);
                                } else {
                                    setSelectedLogState(null);
                                }
                            } catch (err) {
                                setChatLogs([]);
                            } finally {
                                setLoading(false);
                            }
                        })();
                    }}
                    disabled={loading}
                >
                    Reset
                </Button>
                {/* Export options dropdown anchored to Export button */}
                <Menu
                    anchorEl={exportAnchorEl}
                    open={Boolean(exportAnchorEl)}
                    onClose={handleExportClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={exportAsCSV}>Export as CSV</MenuItem>
                    <MenuItem onClick={exportAsJSON}>Export as JSON</MenuItem>
                    <MenuItem onClick={handleExportClose} sx={{ color: 'secondary.main' }}>Cancel</MenuItem>
                </Menu>
            </Box>

            {/* Second Row */}
            <Box sx={{ display: 'flex', width: '100%', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>

                {/* Left Section: Display chatLogs */}
                <Box sx={{ width: '15%', border: '1px solid #888', p: 2, overflowY: 'auto' }}>
                    <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>Logs</Typography>
                    </Box>
                    {loading ? (
                        <Typography variant="caption" sx={{ textAlign: 'left' }}>Loading...</Typography>
                    ) : filteredLogs.length === 0 ? (
                        <Typography variant="caption" sx={{ textAlign: 'left' }}>No logs found.</Typography>
                    ) : (
                        filteredLogs.map(log => {
                            const internalVal = log.internal !== undefined ? log.internal : sessionInternalMap[log.sessionId];
                            const aiModeVal = log.aiMode !== undefined ? log.aiMode : sessionAiModeMap[log.sessionId];
                            const channel = getChannelFromSessionId(log.sessionId);
                            let SessionIcon = null;
                            if (channel === 'teams') {
                                SessionIcon = <img src={MsTeamsSvg} alt="Teams" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />;
                            } else if (channel === 'slack') {
                                SessionIcon = <img src={SlackSvg} alt="Slack" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />;
                            } else if (channel === 'email') {
                                SessionIcon = <EmailIcon fontSize="small" sx={{ mr: 0.75, verticalAlign: 'middle', color: '#1565C0' }} />;
                            } else if (channel === 'whatsapp') {
                                SessionIcon = <WhatsAppChannelIcon sx={{ mr: 0.75 }} />;
                            } else if (internalVal === true) {
                                SessionIcon = <ScienceIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />;
                            } else if (aiModeVal === true) {
                                SessionIcon = <AutoAwesomeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#9C27B0' }} />;
                            } else if (internalVal === false) {
                                SessionIcon = <LanguageIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: 'success.main' }} />;
                            }
                            return (
                                <Box
                                    key={log.id}
                                    sx={{ mb: 2, p: 1, borderBottom: '1px solid #eee', cursor: 'pointer', bgcolor: selectedLog && selectedLog.id === log.id ? '#e3f2fd' : 'inherit' }}
                                    onClick={() => setSelectedLog(log)}
                                >
                                    <Typography variant="body2" sx={{ textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                        {SessionIcon}Agent: {agentMap[log.agentId] || log.agentId}
                                    </Typography>
                                    <Typography variant="caption" sx={{ textAlign: 'left', width: '100%', display: 'block', p: 0, m: 0 }}>{formatDate(log.dateCreated)}</Typography>
                                </Box>
                            );
                        })
                    )}
                </Box>
                {/* Middle Section */}
                <Box sx={{ width: '60%', border: '1px solid #888', p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', position: 'relative', minHeight: 0 }}>
                    {/* Middle Section Inner Column */}
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                        {/* Chat History Header */}
                        <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>Chat History</Typography>
                            {selectedLog && (
                                <Tooltip title={selectedLog.chatHistoryText ? 'Copy chat history' : 'No chat history to copy'}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={handleCopyChatHistory}
                                            disabled={!selectedLog.chatHistoryText}
                                            sx={{ color: '#fff', p: 0.25 }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                        {/* Chat details for selected log */}
                        <Box sx={{ width: '100%', mt: 1, height: '100%', minHeight: 0, position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column' }}>
                            {loading ? (
                                <Typography variant="caption">Loading...</Typography>
                            ) : selectedLog ? (
                                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                                    <Box sx={{ maxHeight: '100%', overflowY: 'auto', px: 1, minHeight: 0, position: 'relative', zIndex: 1 }}>
                                        {(() => {
                                            const parsedChat = parseChatData(selectedLog.chatData);
                                            // Sort by timestamp (ascending - oldest first)
                                            const sortedChat = Array.isArray(parsedChat) ? [...parsedChat].sort((a, b) => {
                                                const tsA = a.timestamp || '';
                                                const tsB = b.timestamp || '';
                                                if (!tsA && !tsB) return 0;
                                                if (!tsA) return 1; // items without timestamp go to end
                                                if (!tsB) return -1; // items without timestamp go to end
                                                return new Date(tsA).getTime() - new Date(tsB).getTime();
                                            }) : [];
                                            // Helper to normalize text
                                            const normalize = str => (str || '').trim().toLowerCase();
                                            return sortedChat.length > 0 ? (
                                                sortedChat.map((msg, idx) => {
                                                    // Only match user questions
                                                    if (msg.role !== 'user') {
                                                        const followingUserMessage = sortedChat.slice(idx + 1).find(message =>
                                                            typeof message.role === 'string' && message.role.toLowerCase() === 'user'
                                                        );
                                                        return (
                                                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.1 }}>
                                                                <Box sx={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                                    <Box sx={{ bgcolor: '#e3f2fd', color: 'text.primary', px: 2, py: 1, borderRadius: 2, width: '100%', boxShadow: 1 }}>
                                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: msg.text || msg.content || JSON.stringify(msg) }} />
                                                                    </Box>
                                                                    <HistoricalQuickReplies
                                                                        quickReplies={msg.quickReplies}
                                                                        followingUserMessage={followingUserMessage}
                                                                    />
                                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, mr: 0.5, alignSelf: 'flex-end', textAlign: 'right' }}>
                                                                        {formatDate(msg.timestamp)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    }
                                                    const questionText = normalize(msg.text || msg.content);
                                                    // Find Q&A match
                                                    const qaMatch = qaList.find(qa => normalize(qa.question) === questionText);
                                                    // Find unanswered match (only if no Q&A match)
                                                    const unansweredMatch = !qaMatch ? unansweredList.find(uq => normalize(uq.question) === questionText) : null;
                                                    // Highlight style
                                                    const highlightStyle = qaMatch || unansweredMatch ? { border: '2px solid #1976d2', boxShadow: '0 0 8px #1976d2' } : {};
                                                    // Button logic (training hidden for chat_log_access_only users)
                                                    let trainBtn = null;
                                                    if (!chatLogAccessOnly) {
                                                        if (qaMatch) {
                                                            trainBtn = (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    sx={{ mb: 1, mr: 1 }}
                                                                    onClick={() => {
                                                                        setQaModalMode('edit');
                                                                        setQaModalData(qaMatch);
                                                                        setQaModalOpen(true);
                                                                    }}
                                                                >
                                                                    Train Q&A
                                                                </Button>
                                                            );
                                                        } else if (unansweredMatch) {
                                                            trainBtn = (
                                                                <Button
                                                                    variant="contained"
                                                                    color="secondary"
                                                                    size="small"
                                                                    sx={{ mb: 1, mr: 1 }}
                                                                    onClick={() => {
                                                                        setQaModalMode('answer');
                                                                        setQaModalData(unansweredMatch);
                                                                        setQaModalOpen(true);
                                                                    }}
                                                                >
                                                                    Train Unanswered Question
                                                                </Button>
                                                            );
                                                        }
                                                    }
                                                    // Determine whether to show the create-from-interaction icon
                                                    let showCreateIcon = false;
                                                    const nextMsg = sortedChat[idx + 1];
                                                    if (!chatLogAccessOnly && !qaMatch && !unansweredMatch && nextMsg && nextMsg.role === 'bot') {
                                                        // Check minimal trainable heuristic
                                                        if (isTrainableUserMessage(msg, nextMsg)) {
                                                            showCreateIcon = true;
                                                        }
                                                    }
                                                    return (
                                                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5 }}>
                                                            <Box sx={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                                <Box sx={{ bgcolor: '#f5f5f5', color: 'text.primary', px: 2, py: 1, borderRadius: 2, width: '100%', boxShadow: 1, position: 'relative', ...highlightStyle }}>
                                                                    {/* training icon inside bubble, top-right */}
                                                                    {showCreateIcon && (
                                                                        <CastForEducationIcon
                                                                            fontSize="medium"
                                                                            sx={{ position: 'absolute', top: 6, right: 6, cursor: 'pointer', color: theme.palette.primary.main, backgroundColor: '#fff', borderRadius: '50%', p: 0.25 }}
                                                                            titleAccess="Train this interaction"
                                                                            onClick={() => {
                                                                                const next = sortedChat[idx + 1];
                                                                                // Prefer agentId on the bot message, then session-level agent, then current selected agent
                                                                                const inferredAgentId = next?.agentId || selectedLog?.agentId || selectedAgent || '';
                                                                                // Try to extract kbIds from the bot message if available
                                                                                const inferredKbIds = next?.kbIds || (next?.kbId ? [next.kbId] : []) || [];
                                                                                setQaModalMode('create');
                                                                                setQaModalData({ question: msg.text || msg.content, answer: next?.text || next?.content, agentId: inferredAgentId, kbIds: inferredKbIds });
                                                                                setQaModalOpen(true);
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <Box sx={{ pr: showCreateIcon ? '28px' : 0 }}>
                                                                        {trainBtn}
                                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'left', fontWeight: qaMatch || unansweredMatch ? 600 : 400 }} dangerouslySetInnerHTML={{ __html: msg.text || msg.content || JSON.stringify(msg) }} />
                                                                    </Box>
                                                                </Box>
                                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, ml: 0.5, alignSelf: 'flex-start', textAlign: 'left' }}>
                                                                    {formatDate(msg.timestamp)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No chat data available.</Typography>
                                            );
                                        })()}
                                        {/* QAModal for training Q&A/unanswered */}
                                        <QAModal
                                            open={qaModalOpen && !chatLogAccessOnly}
                                            onClose={() => setQaModalOpen(false)}
                                            mode={qaModalMode}
                                            qaData={qaModalData}
                                            onSave={handleQASave}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary">Select a chat log to view details.</Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                {/* Right Section: ChatLog Details */}
                <Box sx={{ width: '25%', border: '1px solid #888', p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {/* ChatLog Details Header */}
                    <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>Chat Log Details</Typography>
                    </Box>
                    {loading ? (
                        <Typography variant="caption" sx={{ textAlign: 'left' }}>Loading...</Typography>
                    ) : selectedLog ? (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Session ID:</strong> {selectedLog.sessionId}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Date Created:</strong> {formatDate(selectedLog.dateCreated)}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Date Updated:</strong> {formatDate(selectedLog.dateUpdated)}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Input Tokens:</strong> {selectedLog.inputTokens ?? '-'}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Output Tokens:</strong> {selectedLog.outputTokens ?? '-'}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Chat Rating:</strong> {selectedLog.chatRating ?? '-'}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Chat Rating Comments:</strong> {selectedLog.chatRatingComments ?? '-'}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>End User IP:</strong> {sessionLogData?.chatSession?.endUserIp || '-'}</Typography>
                            {(() => {
                                const geoLine = formatEndUserGeo(sessionLogData?.chatSession);
                                if (!geoLine) return null;
                                return (
                                    <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}>
                                        {geoLine}
                                    </Typography>
                                );
                            })()}
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Voice Used:</strong> {sessionLogData?.chatSession != null ? (sessionLogData.chatSession.hasVoice ? 'Yes' : 'No') : '-'}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Chat Transcript Sent To:</strong> {selectedLog.visitorEmail ?? '-'}</Typography>
                            <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1, mt: 2, mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>Chat Summary</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                                {selectedLog.chatSummary || 'No summary available.'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}>
                                <strong>Classified Department:</strong>{' '}
                                {selectedLog.classifiedDepartmentName || '-'}
                            </Typography>
                            {/* Always show chatSession details titles, use '-' if not loaded */}
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Session Type:</strong> {(() => {
                                const channel = getChannelFromSessionId(selectedLog?.sessionId);
                                if (channel === 'teams') return 'Teams';
                                if (channel === 'slack') return 'Slack';
                                if (channel === 'email') return 'Email';
                                if (channel === 'whatsapp') return 'WhatsApp';
                                if (sessionLogData?.chatSession?.aiMode) return 'Ai Mode';
                                if (sessionLogData?.chatSession?.internal !== undefined) return sessionLogData.chatSession.internal ? 'Internal Test' : 'Live Visitor';
                                return '-';
                            })()}</Typography>
                            <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Messages Count:</strong> {sessionLogData && sessionLogData.chatSession ? (sessionLogData.chatSession.messagesCount ?? '-') : '-'}</Typography>
                            {/* Always show User Details titles, use '-' if not loaded */}
                            <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1, mt: 2, mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>User Details:</Typography>
                            </Box>
                            {(() => {
                                // Parse formData as JSON if possible
                                let userDetails = {};
                                if (sessionLogData && sessionLogData.chatForm && sessionLogData.chatForm.formData) {
                                    try {
                                        userDetails = JSON.parse(sessionLogData.chatForm.formData);
                                    } catch {
                                        userDetails = {};
                                    }
                                }
                                // If no userDetails, show a default set (or just a dash)
                                const keys = Object.keys(userDetails).length > 0 ? Object.keys(userDetails) : ['Name', 'Email', 'Message', 'Mobile Number'];
                                return keys.map(key => (
                                    <Typography key={key} variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>{key}:</strong> {userDetails[key] || '-'}</Typography>
                                ));
                            })()}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'left' }}>Select a chat log to view details.</Typography>
                    )}
                </Box>
            </Box>
            {processingQA && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(255,255,255,0.5)',
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(3px)'
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: '#1976d2',
                            color: '#fff',
                            px: 4,
                            py: 3,
                            borderRadius: 2,
                            boxShadow: 3,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 20,
                            fontWeight: 500,
                            letterSpacing: 1
                        }}
                    >
                        Training in progress. This may take a few moments...
                    </Box>
                </Box>
            )}
        </Box>
    );
}
